import React, {useEffect, useState} from "react";

import {
	RadarChart,
	PolarGrid,
	PolarAngleAxis,
	PolarRadiusAxis,
	Radar,
	Tooltip,
	Legend,
} from "recharts";

import {getPlayerStructure} from "@/utils/functions";
import {
	initialValuationParameters,
	initialBaseValuationParameters,
} from "@/utils/constants";
import {useCategories} from "@/utils/hooks";

import "./index.scss";
import {useTranslation} from "react-i18next";
import {COLORS} from "@/style/utils";

export default function PlayerRadarChart(props) {
	const {playerData, valuationsData, color, multiPlayerData, players} = props;

	const {t} = useTranslation();

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
	});

	const [radarData, setRadarData] = useState([]);
	useEffect(() => {
		// Se multiPlayerData è fornito, non calcolare i dati
		if (multiPlayerData) {
			setRadarData(multiPlayerData);
			return;
		}

		if (valuationsData?.length && playerData) {
			const avgAreas = {};

			const playerCategory = categories?.filter(
				(c) => c.id === playerData?.playerCategoryId,
			)?.[0];

			valuationsData.forEach((v) => {
				const parameters =
					!playerCategory?.basicActivities ||
					playerData?.playerCategoryName === "Under 13"
						? initialValuationParameters(t)
						: initialBaseValuationParameters(t);

				parameters.forEach((p) => {
					if (!avgAreas[p.area]) {
						avgAreas[p.area] = {};
					}

					avgAreas[p.area].total =
						(avgAreas[p.area].total || 0) + (v[p.fieldKeyRating] || 0);
					avgAreas[p.area].elements =
						(avgAreas[p.area].elements || 0) + (v[p.fieldKeyRating] ? 1 : 0);
				});
			});

			const radarData = [];
			Object.keys(avgAreas).forEach((area) => {
				radarData.push({
					subject: String(area).charAt(0).toUpperCase() + String(area).slice(1),
					A: avgAreas[area]?.elements
						? (avgAreas[area]?.total || 0) / avgAreas[area]?.elements
						: 0,
					fullMark: 10,
				});
			});

			radarData.push({
				subject: "Struttura",
				A: getPlayerStructure(
					playerData?.playerCategoryName || "",
					playerData?.lastHeight,
					playerData?.lastWeight,
				),
				fullMark: 10,
			});

			setRadarData(radarData);
		}
	}, [valuationsData, categories, multiPlayerData, playerData, t]);

	const getPrimaryColor = () => {
		const primaryColor =
			getComputedStyle(document.documentElement)
				.getPropertyValue("--color-primary")
				.trim() || "#1059bf";
		return primaryColor;
	};

	const hexToRgba = (hex, alpha) => {
		if (!hex || !hex.startsWith("#")) return `rgba(16, 89, 191, ${alpha})`;
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		return `rgba(${r}, ${g}, ${b}, ${alpha})`;
	};

	const primaryColor = getPrimaryColor();
	const chartColor = color || primaryColor;
	const chartColorRgba = (alpha) => {
		if (chartColor.startsWith("#")) {
			return hexToRgba(chartColor, alpha);
		}
		return chartColor.replace("rgb", "rgba").replace(")", `, ${alpha})`);
	};

	// Se multiPlayerData è fornito, usa quello invece di radarData
	const dataToUse = multiPlayerData || radarData;
	const hasData = multiPlayerData
		? multiPlayerData.length > 0
		: valuationsData?.length && radarData.length > 0;

	return (
		<div className="player-radar-chart-container">
			{hasData ? (
				<RadarChart
					outerRadius={100}
					width={500}
					height={multiPlayerData ? 300 : 250}
					data={dataToUse}
				>
					<PolarGrid
						stroke={chartColorRgba(0.15)}
						strokeWidth={1.5}
						polarRadius={[20, 40, 60, 80, 100]}
					/>
					<PolarAngleAxis
						dataKey="subject"
						tick={{
							fill: COLORS.text,
							fontSize: 13,
							fontWeight: 600,
							letterSpacing: "0.01em",
						}}
						stroke={chartColorRgba(0.2)}
					/>
					<PolarRadiusAxis
						angle={90}
						domain={[0, 10]}
						tick={{
							fill: COLORS.textSecondary,
							fontSize: 11,
							fontWeight: 500,
						}}
						stroke={chartColorRgba(0.15)}
					/>
					{multiPlayerData && players && players.length > 0 ? (
						// Multi-player mode
						<>
							{players.map((player, index) => (
								<Radar
									key={index}
									name={player.name}
									dataKey={`player${index + 1}`}
									stroke={player.color}
									fill={player.color}
									fillOpacity={0.3}
									strokeWidth={2.5}
									dot={{
										fill: player.color,
										strokeWidth: 2,
										r: 4,
									}}
									activeDot={{
										r: 6,
										fill: player.color,
										stroke: COLORS.surface,
										strokeWidth: 2,
									}}
								/>
							))}
							<Legend />
						</>
					) : (
						// Single player mode
						<Radar
							name="Valore"
							dataKey="A"
							stroke={chartColor}
							fill={chartColor}
							fillOpacity={0.4}
							strokeWidth={2.5}
							dot={{
								fill: chartColor,
								strokeWidth: 2,
								r: 4,
							}}
							activeDot={{
								r: 6,
								fill: chartColor,
								stroke: COLORS.surface,
								strokeWidth: 2,
							}}
						/>
					)}
					<Tooltip
						contentStyle={{
							backgroundColor: "rgba(255, 255, 255, 0.98)",
							border: `1.5px solid ${chartColorRgba(0.25)}`,
							borderRadius: "12px",
							boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
							padding: "10px 14px",
							fontSize: "13px",
							fontWeight: 500,
							color: COLORS.text,
						}}
						labelStyle={{
							fontWeight: 600,
							color: chartColor,
							marginBottom: "6px",
							fontSize: "14px",
						}}
						cursor={{
							stroke: chartColor,
							strokeWidth: 2,
							strokeDasharray: "5 5",
						}}
						formatter={(value) => {
							if (typeof value === "number") {
								return value.toFixed(2);
							}
							return value;
						}}
					/>
				</RadarChart>
			) : (
				<div className="radar-chart-empty">Grafico non disponibile</div>
			)}
		</div>
	);
}
